import DmCoreError from '../errors/DmCoreError'

export const DEFAULT_TIMEOUT_MS = 10_000

export interface DmCoreRequestConfig {
  baseUrl: string
  getAccessToken?: () => string | undefined | null
  timeoutMs?: number
}

export interface RequestOptions {
  method: 'GET' | 'POST'
  path: string
  body?: unknown
  /** Extra headers merged into the request (e.g. X-Api-Key). */
  headers?: Record<string, string>
  /** When false, skip Authorization even if a token is available. Default true. */
  auth?: boolean
}

const joinUrl = (baseUrl: string, path: string): string => {
  const base = baseUrl.replace(/\/+$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalizedPath}`
}

const parseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text()
  if (!text) {
    return undefined
  }

  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

export const createRequest = (config: DmCoreRequestConfig) => {
  const timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS

  return async <T>(options: RequestOptions): Promise<T> => {
    const headers: Record<string, string> = { ...options.headers }

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    if (options.auth !== false) {
      const token = config.getAccessToken?.()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
    }

    const response = await fetch(joinUrl(config.baseUrl, options.path), {
      method: options.method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: AbortSignal.timeout(timeoutMs)
    })

    if (!response.ok) {
      const body = await parseBody(response)
      throw new DmCoreError(
        `DM Core request failed with status ${response.status}`,
        response.status,
        body
      )
    }

    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    if (!text) {
      return undefined as T
    }

    return JSON.parse(text) as T
  }
}

export type DmCoreRequest = ReturnType<typeof createRequest>
